import { Fragment, type ComponentChildren } from 'preact';
import type {
  LiveConfig,
  LiveConsentSegment,
  LiveOutcome,
  LiveQuestion,
  LiveSignup,
} from './config';
import { MAX_EMAIL_CHARS } from './subscriptions';
import {
  behaviourFor,
  hasAnswer,
  isCorrect,
  isScorable,
  MAX_VALUE_CHARS,
  revealsAnswer,
  wantsLongText,
  type Answer,
} from './engine';
import type { Share } from './results';

/**
 * The whole renderer. One component tree covers every state any format can be in, because a state
 * that is a separate mount is a state that can flash or shift the page.
 *
 * There is no per format component. A question is drawn from its type, a result screen is drawn
 * from what the format produced, and the differences between a poll and a survey live in the
 * behaviour table in engine.ts rather than in five copies of this file.
 */
export type ViewState =
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'question';
      readonly config: LiveConfig;
      readonly index: number;
      readonly answer: Answer | undefined;
      readonly revealed: boolean;
      readonly busy: boolean;
      readonly error: string | null;
    }
  | {
      readonly kind: 'results';
      readonly config: LiveConfig;
      readonly chosen: string | null;
      readonly total: number;
      readonly shares: readonly Share[];
    }
  | {
      readonly kind: 'score';
      readonly config: LiveConfig;
      readonly score: number;
      readonly max: number;
    }
  | { readonly kind: 'outcome'; readonly config: LiveConfig; readonly outcome: LiveOutcome }
  | { readonly kind: 'thanks'; readonly config: LiveConfig }
  | { readonly kind: 'failed'; readonly config: LiveConfig; readonly message: string };

/**
 * The signup step, as the view needs it. `done` is what the endpoint said happened, not what the
 * publisher's settings say should happen.
 */
export interface SignupView {
  readonly signup: LiveSignup;
  readonly email: string;
  readonly ticked: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  readonly done: 'pending' | 'subscribed' | null;
}

export interface ViewActions {
  choose(optionId: string): void;
  setText(value: string): void;
  setNumber(value: number): void;
  next(): void;
  back(): void;
  retry(): void;
  setEmail(value: string): void;
  toggleConsent(): void;
  subscribe(): void;
}

function votes(total: number): string {
  return total === 1 ? '1 vote' : `${total.toLocaleString('en-GB')} votes`;
}

/**
 * Consent wording, rendered as nodes.
 *
 * Each segment is a run of text, so it becomes a text node, an anchor when it carries an href and
 * an emphasis element when it carries emphasis. Nothing is ever parsed and `innerHTML` is never
 * touched, which is the point of the document carrying segments instead of markup (D27).
 *
 * Links open in a new tab because the reader is mid interaction on somebody's article, and
 * `rel="noopener noreferrer"` because a publisher's privacy notice is still a page we are handing
 * a window reference to.
 */
export function ConsentText(props: { segments: readonly LiveConsentSegment[] }) {
  return (
    <>
      {props.segments.map((segment, at) => {
        const inner =
          segment.emphasis === 'strong' ? (
            <strong>{segment.text}</strong>
          ) : segment.emphasis === 'em' ? (
            <em>{segment.text}</em>
          ) : (
            segment.text
          );
        const key = `${at}`;
        return segment.href === undefined ? (
          <Fragment key={key}>{inner}</Fragment>
        ) : (
          <a key={key} href={segment.href} target="_blank" rel="noopener noreferrer">
            {inner}
          </a>
        );
      })}
    </>
  );
}

/**
 * The optional newsletter step.
 *
 * The tick is a separate, positive act: never pre ticked, never bundled with the interaction the
 * reader came for, and it is the only thing that arms the button. Under PECR neither a pre ticked
 * box nor consent bundled into something else is consent at all.
 *
 * The button stays in the tab order while it is inert, with a line saying what it is waiting for,
 * because a control that is disabled and silent tells a reader nothing about how to proceed.
 */
function SignupStep(props: { view: SignupView; actions: ViewActions }) {
  const { signup, email, ticked, busy, error, done } = props.view;

  if (done !== null) {
    return (
      <div class="qz-su">
        <p class="qz-su-done" role="status">
          {done === 'pending'
            ? 'Almost there. We have sent you an email: you are subscribed once you click the link in it.'
            : 'Thanks, you are subscribed.'}
        </p>
      </div>
    );
  }

  return (
    <div class="qz-su">
      <p class="qz-su-h">{signup.heading}</p>
      <input
        class="qz-text"
        type="email"
        autocomplete="email"
        inputMode="email"
        maxLength={MAX_EMAIL_CHARS}
        value={email}
        disabled={busy}
        aria-label="Your email address"
        placeholder="you@example.com"
        onInput={(event) => props.actions.setEmail((event.target as HTMLInputElement).value)}
      />
      <label class="qz-su-c">
        <input
          class="qz-su-tick"
          type="checkbox"
          checked={ticked}
          disabled={busy}
          onChange={() => props.actions.toggleConsent()}
        />
        <span>
          <ConsentText segments={signup.consent} />
        </span>
      </label>
      <button
        type="button"
        class="qz-btn"
        disabled={busy}
        aria-disabled={!ticked}
        aria-describedby={ticked ? undefined : 'qz-su-hint'}
        onClick={() => props.actions.subscribe()}
      >
        {signup.button_label}
      </button>
      <div aria-live="polite">
        {ticked ? null : (
          <p class="qz-su-hint" id="qz-su-hint">
            Tick the box to agree, then we can sign you up.
          </p>
        )}
        {error === null ? null : <p class="qz-err">{error}</p>}
      </div>
    </div>
  );
}

function Frame(props: {
  config: LiveConfig | null;
  actions: ViewActions;
  /** Only the completion screens pass one: the step is what a reader sees after they finish. */
  signup?: SignupView | null | undefined;
  children: ComponentChildren;
}) {
  const credit = props.config?.presentation.show_brand_credit === true;
  const consent = props.config?.consent;
  const signup = props.signup ?? null;
  return (
    <div class="qz" part="widget">
      {props.config === null ? null : <p class="qz-t">{props.config.title}</p>}
      {props.config?.introduction === undefined ? null : (
        <p class="qz-i">{props.config.introduction}</p>
      )}
      {props.children}
      {signup === null ? null : <SignupStep view={signup} actions={props.actions} />}
      {consent === undefined ? null : (
        <p class="qz-consent">
          <ConsentText segments={consent.segments} />
        </p>
      )}
      {credit ? <p class="qz-credit">Powered by quizee</p> : null}
    </div>
  );
}

function Skeleton() {
  return (
    <div class="qz" aria-busy="true">
      <div class="qz-sk">
        <div class="qz-bar qz-w1" />
        <div class="qz-bar qz-w2" />
        <div class="qz-bar qz-w3" />
        <div class="qz-bar qz-w3" />
      </div>
    </div>
  );
}

/** Where the reader is. Announced as a status so a screen reader hears it on every move. */
function Progress(props: { index: number; total: number }) {
  const done = (props.index / props.total) * 100;
  return (
    <div class="qz-prog" role="status">
      <span>
        Question {props.index + 1} of {props.total}
      </span>
      <span class="qz-track" aria-hidden="true">
        <span class="qz-grow" style={`width:${done.toFixed(1)}%`} />
      </span>
    </div>
  );
}

/** The reveal class for one option once a trivia answer has been locked in. */
function optionClass(
  question: LiveQuestion,
  option: { id: string; is_correct?: boolean | undefined },
  selected: boolean,
  revealed: boolean,
): string {
  if (!revealed || !isScorable(question)) return selected ? 'qz-opt qz-sel' : 'qz-opt';
  if (option.is_correct === true) return 'qz-opt qz-ok';
  return selected ? 'qz-opt qz-no' : 'qz-opt';
}

function Options(props: {
  question: LiveQuestion;
  answer: Answer | undefined;
  selectable: boolean;
  revealed: boolean;
  busy: boolean;
  onChoose: (optionId: string) => void;
}) {
  const chosen =
    props.answer !== undefined && props.answer.kind === 'choice' ? props.answer.option_ids : [];
  const multiple = props.question.type === 'multiple_choice';
  const scorable = isScorable(props.question);

  return (
    <ul class="qz-opts">
      {props.question.options.map((option) => {
        const selected = chosen.includes(option.id);
        const mark = props.revealed && scorable ? (option.is_correct === true ? '✓' : '✗') : null;
        return (
          <li key={option.id}>
            <button
              type="button"
              class={optionClass(props.question, option, selected, props.revealed)}
              disabled={props.busy || props.revealed}
              {...(props.selectable || multiple ? { 'aria-pressed': selected } : {})}
              onClick={() => props.onChoose(option.id)}
            >
              {option.label}
              {mark === null || (!selected && option.is_correct !== true) ? null : (
                <span class="qz-mark" aria-hidden="true">
                  {mark}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Scale(props: {
  question: LiveQuestion;
  answer: Answer | undefined;
  busy: boolean;
  onPick: (value: number) => void;
}) {
  const scale = props.question.scale;
  if (scale === undefined) return null;
  const current =
    props.answer !== undefined && props.answer.kind === 'number' ? props.answer.value : null;
  const span = scale.max - scale.min;

  // A wide range is a slider; a narrow one is a row of targets, which is far easier to tap.
  if (span > 12) {
    return (
      <div>
        <input
          class="qz-text"
          type="range"
          min={scale.min}
          max={scale.max}
          step={1}
          value={current ?? scale.min}
          disabled={props.busy}
          aria-label={props.question.prompt}
          onInput={(event) => props.onPick(Number((event.target as HTMLInputElement).value))}
        />
        <p class="qz-ends">
          <span>{scale.min_label ?? scale.min}</span>
          <span>{current === null ? '' : current}</span>
          <span>{scale.max_label ?? scale.max}</span>
        </p>
      </div>
    );
  }

  const steps: number[] = [];
  for (let value = scale.min; value <= scale.max; value += 1) steps.push(value);

  return (
    <div>
      <div class="qz-scale" role="group" aria-label={props.question.prompt}>
        {steps.map((value) => (
          <button
            key={value}
            type="button"
            class={value === current ? 'qz-step qz-sel' : 'qz-step'}
            aria-pressed={value === current}
            disabled={props.busy}
            onClick={() => props.onPick(value)}
          >
            {value}
          </button>
        ))}
      </div>
      {scale.min_label === undefined && scale.max_label === undefined ? null : (
        <p class="qz-ends">
          <span>{scale.min_label ?? ''}</span>
          <span>{scale.max_label ?? ''}</span>
        </p>
      )}
    </div>
  );
}

function FreeText(props: {
  question: LiveQuestion;
  answer: Answer | undefined;
  long: boolean;
  busy: boolean;
  onType: (value: string) => void;
}) {
  const value =
    props.answer !== undefined && props.answer.kind === 'text' ? props.answer.value : '';
  const shared = {
    class: 'qz-text',
    value,
    disabled: props.busy,
    maxLength: MAX_VALUE_CHARS,
    'aria-label': props.question.prompt,
    ...(props.question.placeholder === undefined
      ? {}
      : { placeholder: props.question.placeholder }),
    onInput: (event: Event) => props.onType((event.target as HTMLInputElement).value),
  };
  return props.long ? <textarea {...shared} rows={3} /> : <input {...shared} type="text" />;
}

function Question(props: {
  state: Extract<ViewState, { kind: 'question' }>;
  actions: ViewActions;
}) {
  const { config, index, answer, revealed, busy, error } = props.state;
  const question = config.questions[index];
  if (question === undefined) return <Skeleton />;

  const behaviour = behaviourFor(config.format);
  const total = config.questions.length;
  const last = index === total - 1;
  const autoAdvances =
    behaviour.autoAdvance && question.type === 'single_choice' && !revealsAnswer(config);
  // The one case with no Next button is a question that moves on the moment it is answered and
  // that has to be answered. Anything else needs a way forward, including a skip.
  const showNext = !autoAdvances || !question.required || revealed;
  // Pressing next on an unrevealed trivia question shows the answer rather than moving on, so the
  // label has to say what the button is about to do.
  const willReveal =
    revealsAnswer(config) && isScorable(question) && !revealed && hasAnswer(answer);
  const nextLabel = willReveal || !last ? 'Next' : behaviour.aggregate ? 'See results' : 'Finish';
  const correct = revealed && isCorrect(question, answer);

  return (
    <Frame config={config} actions={props.actions}>
      {total > 1 ? <Progress index={index} total={total} /> : null}
      {question.image_url === undefined ? null : (
        <img class="qz-img" src={question.image_url} alt="" loading="lazy" />
      )}
      <p class="qz-q" tabIndex={-1} data-qz-focus>
        {question.prompt}
        {question.required ? null : <span class="qz-sr"> (optional)</span>}
      </p>

      {question.type === 'free_text' ? (
        <FreeText
          question={question}
          answer={answer}
          long={wantsLongText(question, behaviour)}
          busy={busy}
          onType={props.actions.setText}
        />
      ) : question.type === 'scale' ? (
        <Scale question={question} answer={answer} busy={busy} onPick={props.actions.setNumber} />
      ) : (
        <Options
          question={question}
          answer={answer}
          selectable={!autoAdvances}
          revealed={revealed}
          busy={busy}
          onChoose={props.actions.choose}
        />
      )}

      <div aria-live="polite">
        {revealed && isScorable(question) ? (
          <p class="qz-fb">{correct ? 'Correct' : 'Not quite'}</p>
        ) : null}
        {error === null ? null : <p class="qz-err">{error}</p>}
      </div>

      {index === 0 && !showNext ? null : (
        <div class="qz-nav">
          {index > 0 ? (
            <button type="button" class="qz-back" disabled={busy} onClick={props.actions.back}>
              Back
            </button>
          ) : null}
          {showNext ? (
            <button type="button" class="qz-btn" disabled={busy} onClick={props.actions.next}>
              {nextLabel}
            </button>
          ) : null}
        </div>
      )}
    </Frame>
  );
}

function Results(props: { chosen: string | null; total: number; shares: readonly Share[] }) {
  return (
    <div>
      <ul class="qz-res">
        {props.shares.map((share) => {
          const own = share.id === props.chosen;
          return (
            <li key={share.id} class={own ? 'qz-row qz-own' : 'qz-row'}>
              <div class="qz-fill" style={`width:${(share.fraction * 100).toFixed(1)}%`} />
              <div class="qz-line">
                <span class="qz-label">
                  {share.label}
                  {own ? <span class="qz-you">Your vote</span> : null}
                </span>
                <span class="qz-pct">{share.percent}%</span>
              </div>
            </li>
          );
        })}
      </ul>
      <p class="qz-meta">{votes(props.total)} so far</p>
    </div>
  );
}

/** What the reader is thanked with when no result screen is shown. */
function completion(config: LiveConfig): string {
  if (config.completion_message !== undefined) return config.completion_message;
  return behaviourFor(config.format).aggregate ? 'Thanks for voting.' : 'Thanks for taking part.';
}

export function View(props: {
  state: ViewState;
  actions: ViewActions;
  signup?: SignupView | null | undefined;
}) {
  const { state } = props;
  /**
   * Only the screens a reader lands on having finished. Not the question screens, where asking for
   * an email address would be bundling the newsletter into the thing they came to do, and not the
   * failure screen, where the interaction has not been recorded yet.
   */
  const signup = props.signup ?? null;

  if (state.kind === 'loading') return <Skeleton />;

  if (state.kind === 'question') return <Question state={state} actions={props.actions} />;

  if (state.kind === 'thanks') {
    return (
      <Frame config={state.config} actions={props.actions} signup={signup}>
        <p class="qz-q" tabIndex={-1} data-qz-focus>
          {completion(state.config)}
        </p>
      </Frame>
    );
  }

  if (state.kind === 'failed') {
    return (
      <Frame config={state.config} actions={props.actions}>
        <p class="qz-q" tabIndex={-1} data-qz-focus>
          {state.message}
        </p>
        <div class="qz-nav">
          <button type="button" class="qz-btn" onClick={props.actions.retry}>
            Try again
          </button>
        </div>
      </Frame>
    );
  }

  if (state.kind === 'score') {
    return (
      <Frame config={state.config} actions={props.actions} signup={signup}>
        <div aria-live="polite">
          <p class="qz-score" tabIndex={-1} data-qz-focus>
            {state.score} out of {state.max}
          </p>
          {state.config.completion_message === undefined ? null : (
            <p class="qz-meta">{state.config.completion_message}</p>
          )}
        </div>
      </Frame>
    );
  }

  if (state.kind === 'outcome') {
    return (
      <Frame config={state.config} actions={props.actions} signup={signup}>
        <div aria-live="polite">
          {state.outcome.image_url === undefined ? null : (
            <img class="qz-img" src={state.outcome.image_url} alt="" loading="lazy" />
          )}
          <p class="qz-out" tabIndex={-1} data-qz-focus>
            {state.outcome.title}
          </p>
          {state.outcome.description === undefined ? null : (
            <p class="qz-i">{state.outcome.description}</p>
          )}
        </div>
      </Frame>
    );
  }

  const question = state.config.questions[0];
  return (
    <Frame config={state.config} actions={props.actions} signup={signup}>
      {question === undefined ? null : (
        <p class="qz-q" tabIndex={-1} data-qz-focus>
          {question.prompt}
        </p>
      )}
      <div aria-live="polite">
        <Results chosen={state.chosen} total={state.total} shares={state.shares} />
      </div>
    </Frame>
  );
}
